import * as LDClient from 'launchdarkly-js-client-sdk';

import config from '../../config';
import { authService } from '../auth/AuthService';
import { getBrowser } from '../device/device.utils';
import { isRunningTests } from '../isRunningTests';
import { getLogger } from '../logger/logger';

import {
  AllFeatureFlags,
  LOCAL_FEATURE_FLAGS,
  LocalFeatureFlags,
  REMOTE_FEATURE_FLAGS,
  RemoteFeatureFlagKeys,
  RemoteFeatureFlags,
} from './featureFlags';

const logger = getLogger('[LD]');

const INIT_TIMEOUT_SECONDS = 5;

const withTimeout = (promise: Promise<unknown>, seconds: number, onTimeout: () => void) => {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<void>(resolve => {
    timer = setTimeout(() => {
      onTimeout();
      resolve();
    }, seconds * 1000);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const browserMajorVersion = Number.parseInt(getBrowser.version ?? '', 10);

/** Numeric major so LaunchDarkly rules can target ranges, e.g. `browserMajorVersion <= 16`. */
const browserAttributes = {
  browser: getBrowser.name,
  browserVersion: getBrowser.version,
  browserMajorVersion: Number.isNaN(browserMajorVersion) ? undefined : browserMajorVersion,
};

export const getFlagKey = (name: AllFeatureFlags) => {
  if (LOCAL_FEATURE_FLAGS[name as LocalFeatureFlags]) {
    return undefined;
  }
  return REMOTE_FEATURE_FLAGS[name as RemoteFeatureFlags];
};

export class FeatureFlagManager {
  private streaming = false;
  private changeCallbacks: Record<string, Array<(val: any) => void>> = {};
  private client?: LDClient.LDClient;
  private ready?: Promise<void>;
  private identified?: Promise<void>;

  constructor(_mockClient?: LDClient.LDClient) {
    this.client = _mockClient;
  }

  /**
   * Starts the LaunchDarkly SDK and identifies the logged in user once auth is ready.
   * Idempotent, and never rejects: on failure flags evaluate to their defaults.
   */
  init(): Promise<void> {
    if (this.ready) {
      return this.ready;
    }
    const client = this.getClient();
    const identified = authService.whenReady(async () => {
      // enable targeting single users
      const user = authService.getUserData();
      if (user) {
        try {
          await client.identify({
            kind: 'user',
            key: user.userId, // only send email to LD for internal users
            email: authService.isInternalUser([/@example.com/]) ? user.email : undefined,
            ...browserAttributes,
          });
        } catch (e) {
          logger.log('identify failed, using anonymous flags', e);
        }
      }
    });
    this.identified = identified;
    this.ready = client
      .waitForInitialization(INIT_TIMEOUT_SECONDS)
      .catch(() => logger.log('LD initialization failed, using defaults'))
      // auth readiness never settles if AuthService's own init throws
      .then(() =>
        withTimeout(identified, INIT_TIMEOUT_SECONDS, () => logger.log('auth not ready in time, using anonymous flags'))
      )
      .then(() => logger.log('ready'));
    return this.ready;
  }

  /**
   * Settles once the logged in user (if any) is identified towards LD. May settle after `init()`
   * when auth is slow, and never if auth never becomes ready.
   */
  whenIdentified(): Promise<void> {
    this.init();
    return this.identified ?? Promise.resolve();
  }

  private getClient() {
    if (!this.client) {
      this.client = LDClient.initialize(config.launchDarklyKey, {
        kind: 'user',
        anonymous: true,
        ...browserAttributes,
      });
    }
    return this.client;
  }

  getValue<T = boolean>(key: RemoteFeatureFlagKeys | undefined, defaultValue?: T): T | null {
    if (!key) {
      return null;
    }
    if (!this.client) {
      logger.log('get flag before init, using default', [key, defaultValue]);
      return defaultValue ?? null;
    }

    const value = this.client.variation(key, defaultValue ?? false);
    logger.log(`get flag`, [key, value]);
    return value as T;
  }
  getValueWhenReady<T = boolean>(key: RemoteFeatureFlagKeys | undefined, defaultValue?: T) {
    return this.init().then(() => this.getValue<T>(key, defaultValue));
  }
  notifyOnChanged<T = boolean>(key: RemoteFeatureFlagKeys | undefined, cb: (value: T) => void) {
    if (!key) {
      return;
    }
    this.streamChanges();
    this.changeCallbacks[key] = this.changeCallbacks[key] ?? [];
    this.changeCallbacks[key].push(cb);
    return () => {
      this.changeCallbacks[key] = this.changeCallbacks[key].filter(c => c !== cb);
    };
  }
  private onFlagChanged(flags: RemoteFeatureFlagKeys[]) {
    for (const flag of flags) {
      if (this.changeCallbacks[flag]) {
        const value = this.getValue(flag);
        logger.log('setting callback value', [flag, value]);
        this.changeCallbacks[flag].forEach(cb => cb(value));
      }
    }
  }
  private streamChanges() {
    try {
      if (this.streaming !== true) {
        this.streaming = true;
        this.getClient().on('change', (flags: Record<RemoteFeatureFlagKeys, any>) => {
          this.onFlagChanged(Object.keys(flags) as RemoteFeatureFlagKeys[]);
        });
      }
    } catch {
      /* */
    }
  }
}

/* Mock for testing, override flags with sessionStorage keys */
const ReversedFeatureMap = Object.fromEntries(Object.entries(REMOTE_FEATURE_FLAGS).map(([a, b]) => [b, a]));

class FlagManagerMock extends FeatureFlagManager {
  constructor() {
    // mock LD client
    super({} as any);
  }
  init() {
    return Promise.resolve();
  }
  getValue<T = boolean>(key: RemoteFeatureFlagKeys, defaultValue?: T) {
    const mappedKey = ReversedFeatureMap[key];
    const globalFlags = (globalThis as any)._e2eFlags || {};
    if (mappedKey in globalFlags) {
      // This was only way I could get playwright working
      return globalFlags[mappedKey] as T;
    }
    const value = sessionStorage.getItem(mappedKey);
    if (value) {
      return Boolean(JSON.parse(value)) as T;
    }
    return defaultValue ?? null;
  }
  getValueWhenReady<T = boolean>(key: RemoteFeatureFlagKeys) {
    return Promise.resolve(this.getValue(key) as T);
  }
}

const isTestEnv = isRunningTests();

export const flagManager = isTestEnv ? new FlagManagerMock() : new FeatureFlagManager();
